import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  FileSignature, ShieldCheck, CheckCircle, Clock, AlertTriangle,
} from 'lucide-react';

/**
 * Iter131f — Public Magic-Link Sign page.
 *
 * Route: /sign/:documentId?token=…
 * Rendered without any authentication. The magic link is validated on
 * every fetch; expired / used / mismatched tokens receive a friendly
 * error copy.
 */
const PublicSignPage = () => {
  const { documentId } = useParams();
  const [search] = useSearchParams();
  const token = search.get('token') || '';
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [typedName, setTypedName] = useState('');
  const [ack, setAck] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const fmt = useMemo(() => (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString(); } catch { return iso; } }, []);

  useEffect(() => {
    if (!documentId || !token) { setErr('Missing signing token.'); setLoading(false); return; }
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/public/client-documents/${documentId}?token=${encodeURIComponent(token)}`);
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data?.detail || 'Unable to load document');
        setDoc(data);
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, [documentId, token]);

  const canSubmit = ack && typedName.trim().length >= 3 && doc?.status === 'PENDING';

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/public/client-documents/${documentId}/sign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, typed_name: typedName.trim(), acknowledged: true }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || 'Signing failed');
      setDone(true);
      toast({ title: 'Signed', description: 'Thank you — your signature has been recorded.' });
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#030812] flex items-center justify-center text-[#94A3B8]">Loading…</div>;
  }
  if (err) {
    return (
      <div className="min-h-screen bg-[#030812] flex items-center justify-center p-6">
        <Card className="bg-[#131A2A] border-[#334155] max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <div className="text-[#F8FAFC] text-lg font-semibold mb-1">This link isn't working</div>
            <p className="text-[#94A3B8] text-sm">{err}</p>
            <p className="text-[#94A3B8] text-xs mt-3">Ask your investigator to send a fresh signing link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!doc) return null;

  return (
    <div className="min-h-screen bg-[#030812] p-4 md:p-8" data-testid="public-sign-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded bg-[#0F172A] flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#F4C04A]" />
          </div>
          <div>
            <div className="text-[#F4C04A] uppercase tracking-widest text-[10px] font-bold">NexusTrac · Secure Signing</div>
            <div className="text-[#94A3B8] text-xs">Session token is valid for this one document only.</div>
          </div>
        </div>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6 md:p-8 text-slate-800">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div>
                <h1 className="text-2xl font-bold" data-testid="doc-title">{doc.title}</h1>
                <div className="text-xs text-slate-500 mt-1">Document ID: <span className="font-mono">{doc.id}</span></div>
              </div>
              <Badge className={
                doc.status === 'SIGNED' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                : doc.status === 'REJECTED' ? 'bg-red-100 text-red-800 border border-red-300'
                : 'bg-amber-100 text-amber-800 border border-amber-300'}>
                {doc.status === 'PENDING' ? <><Clock className="w-3 h-3 mr-1 inline" /> Awaiting your signature</>
                : doc.status === 'SIGNED' ? <><CheckCircle className="w-3 h-3 mr-1 inline" /> Signed</>
                : doc.status}
              </Badge>
            </div>
            {doc.description && (
              <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap text-sm text-slate-700">
                {doc.description}
              </div>
            )}
            {doc.body && (
              <div className="mb-4 bg-white border border-slate-200 rounded-lg p-4 whitespace-pre-wrap text-sm text-slate-800 max-h-[420px] overflow-y-auto">
                {doc.body}
              </div>
            )}
            <div className="text-xs text-slate-500 mb-6">
              Sent {fmt(doc.created_at)} · {doc.created_by_name || 'Nexus Investigation Agency'}
            </div>

            {done || doc.status === 'SIGNED' ? (
              <div className="rounded border border-emerald-300 bg-emerald-50 p-4 text-center">
                <CheckCircle className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
                <div className="text-emerald-800 font-semibold text-lg">Signed</div>
                {doc.signature && (
                  <div className="text-emerald-800 text-2xl mt-2" style={{ fontFamily: 'cursive' }}>
                    {doc.signature.typed_name}
                  </div>
                )}
                <p className="text-emerald-700 text-xs mt-2">You can safely close this tab — your investigator has been notified.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} className="mt-0.5" data-testid="public-ack" />
                  <span>I have reviewed this document. My typed name below is my legal signature under the E-SIGN Act.</span>
                </label>
                <div>
                  <label className="text-xs uppercase text-slate-500 tracking-wider">Type your full legal name</label>
                  <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} className="mt-1" data-testid="public-typed-name" />
                  {typedName && (
                    <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded">
                      <div className="text-[10px] uppercase text-slate-400 tracking-wider">Electronic Signature</div>
                      <div className="text-slate-900 text-2xl font-semibold" style={{ fontFamily: 'cursive' }}>{typedName}</div>
                    </div>
                  )}
                </div>
                <Button onClick={submit} disabled={!canSubmit || saving}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 text-base"
                        data-testid="public-sign-btn">
                  <FileSignature className="w-5 h-5 mr-2" />
                  {saving ? 'Signing…' : 'Sign & Submit'}
                </Button>
                <p className="text-[11px] text-slate-500 text-center">
                  Secure link · Expires 7 days after issue · One use only.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicSignPage;
